package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.budget.isUnfilledDemand
import com.evoapps.evoshift.domain.explain.Side
import com.evoapps.evoshift.domain.model.PersonLabour
import com.evoapps.evoshift.domain.model.LabourBase
import com.evoapps.evoshift.domain.model.LabourShare
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Spmh
import com.evoapps.evoshift.domain.model.SpmhPart
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.util.calculateDurationHours
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.plus

/** One day of a month for the dashboards' charts. Actual figures are null until there are any. */
data class DayTrend(
    val date: LocalDate,
    /** The day's revenue target (netto). */
    val revenuePlan: Double,
    /** Takings entered for the day (netto). */
    val revenueActual: Double?,
    /** Employer's labour cost of the planned shifts. */
    val laborPlanned: Double,
    /** Employer's labour cost of the clocked hours; null before anyone clocked out. */
    val laborActual: Double?,
    val hoursPlanned: Double,
    val hoursActual: Double,
    /**
     * SH-54: hours of the day's shifts nobody is assigned to (open or unfilled, still to be
     * filled). SH-193: they are in [hoursPlanned], and at [openCostPerHour] ([openLabour]) in
     * [laborPlanned]; never in the actual.
     */
    val hoursOpen: Double = 0.0,
    /** SH-193: how many shifts [hoursOpen] are. */
    val openShifts: Int = 0,
    /** SH-193: the estimated cost of [hoursOpen] in [laborPlanned]; 0 when they are not priced. */
    val openLabour: Double = 0.0,
    /** SH-193: what [hoursOpen] are estimated at per hour; null: not priced. */
    val openCostPerHour: Double? = null,
    /** SH-95: the commission earned on the day's sales (a fraction; 0 = none, the sales are the income). */
    val commissionPercent: Double = 0.0,
    /** SH-95: the day's share of the fixed revenue, as in its result. */
    val fixedRevenue: Double = 0.0,
    /** Paid shifts of the day nobody could price (no snapshot, no pay rates): not in [laborPlanned] or [hoursPlanned]. */
    val unpricedShifts: Int = 0,
    val unpricedHours: Double = 0.0,
    /** Whose those shifts are. */
    val unpricedUserIds: List<String> = emptyList(),
    /** SH-152: [laborPlanned] and [laborActual] per person (user id), and the unpriced shifts per person. */
    val plannedPeople: Map<String, PersonLabour> = emptyMap(),
    val actualPeople: Map<String, PersonLabour> = emptyMap(),
    val unpricedPeople: Map<String, PersonLabour> = emptyMap(),
    /** SH-206: the day's shifts of people who do not count in the labour figures, per person (listed, not counted). */
    val excludedPeople: Map<String, PersonLabour> = emptyMap()
) {
    /**
     * The labour cost [side] counts on this day (SH-147): the plan its planned shifts; the actual
     * (to date) what was clocked, or the planned shifts once the takings are in (worked as
     * planned), else nothing; the forecast the actual where the takings are in, else the plan.
     */
    fun labourOf(side: Side): Double = when (side) {
        Side.PLAN -> laborPlanned
        Side.ACTUAL -> laborActual ?: if (revenueActual != null) laborPlanned else 0.0
        Side.FORECAST -> labor
    }

    /** SH-152: [labourOf] per person: the same choice of the day's planned or actual shifts. */
    fun peopleOf(side: Side): Map<String, PersonLabour> = when (side) {
        Side.PLAN -> plannedPeople
        Side.ACTUAL -> if (laborActual != null) actualPeople else if (revenueActual != null) plannedPeople else emptyMap()
        Side.FORECAST -> if (revenueActual != null && laborActual != null) actualPeople else plannedPeople
    }

    /** Whether [side] counts this day at all (the actual: once clocked or with takings). */
    fun isIn(side: Side): Boolean = side != Side.ACTUAL || laborActual != null || revenueActual != null

    /** What [laborPercent] is measured against (SH-95). */
    val labourBase: LabourBase get() = LabourShare.baseOf(commissionPercent, fixedRevenue)

    /** The day's sales: the takings where entered, else the target. */
    val sales: Double get() = revenueActual ?: revenuePlan

    /** The labour cost against [sales]: actual where the takings are in, else planned. */
    val labor: Double get() = if (revenueActual != null) laborActual ?: laborPlanned else laborPlanned

    /** The income [sales] bring ([LabourShare]): commission + fixed revenue, or the sales without a commission. */
    val income: Double get() = LabourShare.baseAmount(sales, commissionPercent, fixedRevenue)

    /** Labour cost as a share of the day's income (SH-95): actual where the takings are in, else planned against the target. */
    val laborPercent: Double? get() = LabourShare.percent(labor, income)

    /**
     * SH-191: the day's sales and hours [side]'s SPMH counts: the plan its target and planned
     * hours; the actual its takings and the hours worked (the planned ones while none are counted),
     * nothing before the takings; the forecast the actual where the takings are in, else the plan.
     */
    fun spmhPart(side: Side): SpmhPart {
        // The actual never falls back on unassigned shifts' hours (SH-193): nobody worked them.
        val actual = SpmhPart(revenueActual, Spmh.actualHours(hoursActual, assignedHoursPlanned))
        val plan = SpmhPart(revenuePlan, hoursPlanned)
        return when (side) {
            Side.PLAN -> plan
            Side.ACTUAL -> actual
            Side.FORECAST -> if (revenueActual != null) actual else plan
        }
    }

    /** SH-191: the day's SPMH as the chart shows it ([sales] ÷ its hours): actual where the takings are in, else planned. */
    val spmh: Double?
        get() = if (revenueActual != null) Spmh.actual(revenueActual, hoursActual, assignedHoursPlanned) else Spmh.plan(revenuePlan, hoursPlanned)

    /** SH-193: [hoursPlanned] of the assigned shifts only. */
    val assignedHoursPlanned: Double get() = (hoursPlanned - hoursOpen).coerceAtLeast(0.0)
}

/** SH-191: the days' SPMH of [side]: their sales ÷ their hours ([DayTrend.spmhPart], [Spmh.total]). */
fun List<DayTrend>.spmh(side: Side): Double? = Spmh.total(map { it.spmhPart(side) })

/** A month in the labour-share trend. */
data class MonthTrend(
    val year: Int,
    val month: Int,
    /** Net sales. */
    val revenue: Double,
    val laborCost: Double,
    /** SH-95: the sum of the days' incomes ([DayTrend.income]); the sales without a commission. */
    val income: Double = revenue,
    val labourBase: LabourBase = LabourBase.SALES,
    /** SH-191: the hours [revenue] is divided by for the month's SPMH (worked where the takings are in, else planned). */
    val hours: Double = 0.0
) {
    /** Labour cost as a share of the month's income (SH-95). */
    val laborPercent: Double? get() = LabourShare.percent(laborCost, income)

    /** SH-191: the month's SPMH: its net sales ÷ its [hours]. */
    val spmh: Double? get() = Spmh.of(revenue, hours)
}

/**
 * The days of a roster month as the Daily Records screen builds them: labour from the shifts and
 * their cost snapshots (always current), revenue as entered (or the target). One load per month.
 */
class GetMonthTrendUseCase(
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecord: CalculateDailyRecordUseCase
) {
    suspend operator fun invoke(rosterId: String): Result<List<DayTrend>> = runCatching {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        val (year, month) = RosterIds.yearMonthOf(rosterId)
        val (inputs, entered) = coroutineScope {
            val inputs = async { calculateDailyRecord.load(workplaceId, rosterId).getOrThrow() }
            // Stored records hold what was entered; without access to them the plan still shows.
            val entered = async { financialRepository.getDailyRecordsForRoster(rosterId).getOrDefault(emptyList()) }
            inputs.await() to entered.await().associateBy { it.date }
        }
        generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }
            .takeWhile { it.month.number == month }
            .map { date ->
                val iso = date.toString()
                val day = calculateDailyRecord.compute(workplaceId, rosterId, iso, inputs, DailyRevenueInput.keptFrom(entered[iso]))
                val clocked = day.costs.actualHours > 0
                DayTrend(
                    date = date,
                    revenuePlan = day.revenue.planNetto,
                    revenueActual = day.revenue.realNetto,
                    // SH-193: with the unassigned shifts' estimate; their hours are in hoursPlanned (and hoursOpen).
                    laborPlanned = day.costs.plannedLabour,
                    openLabour = day.costs.openCost,
                    openCostPerHour = day.costs.openCostPerHour,
                    laborActual = if (clocked) day.costs.actualByContractType.values.sum() else null,
                    hoursPlanned = day.costs.plannedHours,
                    hoursActual = day.costs.actualHours,
                    hoursOpen = day.costs.openHours,
                    openShifts = day.costs.openShifts,
                    commissionPercent = day.revenue.commissionPercent,
                    fixedRevenue = day.revenue.fixedRevenue,
                    unpricedShifts = day.costs.unpricedShifts,
                    unpricedHours = day.costs.unpricedHours,
                    unpricedUserIds = day.costs.unpricedUserIds,
                    plannedPeople = day.costs.plannedByPerson,
                    actualPeople = day.costs.actualByPerson,
                    unpricedPeople = day.costs.unpricedByPerson,
                    excludedPeople = day.costs.excludedByPerson
                )
            }
            .toList()
    }

    /**
     * The labour share of each of [rosterIds]' months (combined: actual where takings were entered,
     * else planned), loaded side by side. Months that fail to load are left out.
     */
    suspend fun months(rosterIds: List<String>): List<MonthTrend> = coroutineScope {
        rosterIds.map { id ->
            async {
                invoke(id).getOrNull()?.let { days ->
                    val (year, month) = RosterIds.yearMonthOf(id)
                    MonthTrend(
                        year = year,
                        month = month,
                        revenue = days.sumOf { it.sales },
                        laborCost = days.sumOf { it.labor },
                        income = days.sumOf { it.income },
                        labourBase = if (days.any { it.labourBase == LabourBase.INCOME }) LabourBase.INCOME else LabourBase.SALES,
                        hours = days.sumOf { it.spmhPart(Side.FORECAST).hours }
                    )
                }
            }
        }.awaitAll().filterNotNull().sortedBy { it.year * 100 + it.month }
    }
}

/**
 * SH-147: [this] month's totals (roster_financials, which the server adds up from the stored daily
 * records) with the labour cost of [days], worked out now from the shifts and their cost snapshots,
 * as the daily results, the dashboard's charts and the roster show it. The stored daily records
 * are written when a month is published or repriced, so edits since then (and shifts priced when
 * the month was opened) were not in them: the totals said one labour cost, the other screens
 * another. Plan: every day's planned shifts; actual: what the days count as worked (clocked, or as
 * planned once their takings are in); combined: actual on the days with takings, planned on the
 * rest. Revenue, income and the ledger stay as stored. Unchanged without [days].
 */
fun com.evoapps.evoshift.domain.model.RosterFinancials.withLiveLabour(days: List<DayTrend>): com.evoapps.evoshift.domain.model.RosterFinancials {
    if (days.isEmpty()) return this
    // SH-152: the same per-day choice the explanations list person by person (DayTrend.labourOf).
    val plannedLabour = days.sumOf { it.labourOf(Side.PLAN) }
    val actualLabour = days.sumOf { it.labourOf(Side.ACTUAL) }
    val combinedLabour = days.sumOf { it.labourOf(Side.FORECAST) }
    fun com.evoapps.evoshift.domain.model.FinancialSummary.with(labour: Double) = copy(
        laborCost = labour,
        laborPercentage = if (revenue > 0) labour / revenue * 100 else 0.0
    )
    return copy(planned = planned.with(plannedLabour), actual = actual.with(actualLabour), combined = combined.with(combinedLabour))
}
